'use client';

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, type HTMLMotionProps } from "framer-motion";
import { TbArrowLeft as ArrowLeft, TbArrowRight as ArrowRight } from "react-icons/tb";
import Link from "next/link";
import { cn } from "@/app/lib/utils";
import ServiceStickerBadge from "@/app/components/ui/ServiceStickerBadge";
import { ServiceIcon } from "@/app/components/ui/service-icons";
import QuoteButton from "@/app/components/ui/QuoteButton";

// CVA for card variants - adapted to Nebras branding
const cardVariants = cva(
  "relative flex flex-col justify-between w-full min-h-[340px] p-8 overflow-hidden rounded-2xl border transition-all duration-300 ease-in-out group hover:shadow-2xl",
  {
    variants: {
      variant: {
        default: "border-white/10 bg-white/[0.06] backdrop-blur-md text-white hover:border-brand-text/30 hover:bg-white/[0.08]",
        brand: "border-white/25 bg-white/[0.08] backdrop-blur-[25px] text-white hover:border-brand-cta/40 hover:bg-white/[0.12]",
        dark: "border-white/10 bg-black/30 backdrop-blur-md text-white hover:border-brand-text/40 hover:bg-black/40",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface ServiceCardProps
  extends Omit<HTMLMotionProps<"div">, "title">,
    VariantProps<typeof cardVariants> {
  /**
   * The main title of the card.
   */
  title: string;
  /**
   * The description / paragraph of the service.
   */
  description?: string;
  /**
   * The URL the card's link should point to.
   */
  href: string;
  /**
   * The service slug used to resolve the matching sticker label.
   */
  slug?: string | null;
  /**
   * The source URL for the decorative image.
   */
  imgSrc?: string;
  /**
   * The alt text for the decorative image, for accessibility.
   */
  imgAlt?: string;
  /**
   * The Lucide icon key name to display on the card.
   */
  iconName?: string;
  /**
   * Optional CMS sticker kind override.
   */
  stickerKind?: string | null;
  /**
   * Optional CMS sticker color variant override.
   */
  stickerVariant?: string | null;
  /**
   * The active locale (ar | en) for handling RTL direction and texts.
   */
  locale?: string;
}

const ServiceCard = React.forwardRef<HTMLDivElement, ServiceCardProps>(
  (
    {
      className,
      variant,
      title,
      description,
      href,
      slug,
      imgSrc,
      imgAlt,
      iconName,
      stickerKind,
      stickerVariant,
      locale = "en",
      ...props
    },
    ref
  ) => {
    const isAr = locale === "ar";

    // Animation variants for Framer Motion
    const cardAnimation = {
      hover: {
        scale: 1.02,
        transition: { duration: 0.3 },
      },
    };

    const imageAnimation = {
      hover: {
        scale: 1.12,
        rotate: isAr ? -4 : 4,
        x: isAr ? -8 : 8,
        transition: { duration: 0.4, ease: "easeInOut" },
      },
    };
    
    const arrowAnimation = {
        hover: {
            x: isAr ? -5 : 5,
            transition: { duration: 0.3, ease: "easeInOut", repeat: Infinity, repeatType: "reverse" as const },
        }
    };

    return (
      <motion.div
        className={cn(cardVariants({ variant, className }))}
        ref={ref}
        variants={cardAnimation}
        whileHover="hover"
        dir={isAr ? "rtl" : "ltr"}
        {...props}
      >
        {/* The wrapper is both the sticker's positioning context and StickerPeel's
            drag arena, so it needs real dimensions.

            It previously got `!right-4 !left-auto` with no width. An absolutely
            positioned box with one side auto and no width shrinks to fit its
            content — and its only child is itself absolute, so it collapsed to
            0px. The sticker then laid out from that zero-width edge, sat mostly
            outside the card, and `overflow-hidden` sliced it. The drag clamp
            could not save it either: `constrain()` measures boundsEl.clientWidth,
            which was 0.

            `top-8` is deliberate headroom. The peel flap is positioned above the
            sticker and grows to ~30% of its height on hover; without clearance it
            would peel straight through the top edge and be clipped again. */}
        <ServiceStickerBadge
          slug={slug}
          kind={stickerKind}
          variant={stickerVariant}
          title={title}
          locale={locale}
          align="center"
          sizeClassName="w-12 sm:w-14 md:w-16"
          className={cn(
            "service-card-label-float opacity-90 drop-shadow-[0_16px_28px_rgba(0,0,0,0.28)] group-hover:opacity-100",
            "!top-8 !bottom-6 !w-16 sm:!w-[4.5rem] md:!w-20",
            isAr ? "!left-4 !right-auto" : "!right-4 !left-auto"
          )}
        />

        <div className="relative z-10 flex flex-col h-full justify-between">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="p-3 bg-white/10 rounded-xl border border-white/10 shadow-inner">
                <ServiceIcon slug={slug} iconName={iconName} className="h-8 w-8 text-brand-text" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold tracking-tight text-white group-hover:text-brand-text transition-colors duration-300">
                {title}
              </h3>
              {/* Four lines, not three. The G-02 rewrite made these summaries
                  longer than the old ones, so a 3-line clamp cut them mid-phrase
                  ("…the decisions that come before…") while the card still had
                  empty space above the CTA row. The clamp stays — the full text
                  is one click away — it just no longer truncates earlier than the
                  layout requires. */}
              {description && (
                <p className="mt-3 line-clamp-4 text-sm leading-relaxed text-white/70">
                  {description}
                </p>
              )}
            </div>
          </div>
          
          <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3">
            <Link
              href={href}
              aria-label={isAr ? `تعرف على المزيد حول ${title}` : `Learn more about ${title}`}
              className="inline-flex items-center text-sm font-bold tracking-wider text-brand-text group-hover:text-white transition-colors duration-300"
            >
              {isAr ? "معرفة المزيد" : "LEARN MORE"}
              <motion.div variants={arrowAnimation} className={isAr ? "mr-2" : "ml-2"}>
                  {isAr ? (
                    <ArrowLeft className="h-4 w-4" />
                  ) : (
                    <ArrowRight className="h-4 w-4" />
                  )}
              </motion.div>
            </Link>

            {slug && (
              <QuoteButton
                locale={locale}
                service={slug}
                size="sm"
                variant="outline"
                showIcon={false}
                className="!border-white/25 !text-white/80 hover:!text-brand-cta"
              />
            )}
          </div>
        </div>
        
        {/* Decorative only — the card's meaning is fully carried by the icon,
            title and description, so it is hidden from assistive tech rather than
            announced as a duplicate of the title.

            `object-contain` letterboxed each photo inside the 144px box, so its
            own dark backdrop rendered as a hard-edged grey rectangle sitting on
            the card — it read as a broken image rather than as texture. Cover
            fills the box, and the radial mask fades the edges out so it behaves
            like the ambient wash it was meant to be. */}
        {imgSrc && (
          <motion.img
            src={imgSrc}
            alt=""
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute h-36 w-36 select-none object-cover opacity-[0.18] transition-opacity duration-300 group-hover:opacity-30",
              "[mask-image:radial-gradient(circle_at_center,black_30%,transparent_72%)] [-webkit-mask-image:radial-gradient(circle_at_center,black_30%,transparent_72%)]",
              isAr ? "-left-6 -bottom-6" : "-right-6 -bottom-6"
            )}
            variants={imageAnimation}
          />
        )}
      </motion.div>
    );
  }
);
ServiceCard.displayName = "ServiceCard";

export { ServiceCard };
